import {DataObjectAssignmentType} from "@cm/utils/data-object-assignment"

export type MaterialThumbnailDimensions = {
    size: number
    resolution: number
}

export type MaterialThumbnailAssignmentType =
    | DataObjectAssignmentType.MaterialThumbnail_10x10
    | DataObjectAssignmentType.MaterialThumbnail_15x15
    | DataObjectAssignmentType.MaterialThumbnail_20x20
    | DataObjectAssignmentType.MaterialThumbnail_30x30
    | DataObjectAssignmentType.MaterialThumbnail_7x7Inches
    | DataObjectAssignmentType.MaterialThumbnail_8x8Inches
    | DataObjectAssignmentType.MaterialThumbnail_9x9Inches
    | DataObjectAssignmentType.MaterialThumbnail_13_5x13_5Inches
    | DataObjectAssignmentType.MaterialThumbnail_16x16Inches
    | DataObjectAssignmentType.MaterialThumbnail_27x27Inches
    | DataObjectAssignmentType.MaterialThumbnail_32x32Inches

/**
 * The same eleven thumbnails as `MaterialThumbnailAssignmentType`, named by `MaterialOutputType`
 * instead — the single source for "which output types are thumbnails". Plain strings rather than the
 * enum, which lives in the generated GraphQL SDK: this package must not pull generated code into its
 * dependency graph, and every consumer here compares against the string value anyway.
 *
 * A twelfth size means adding it here as well as to the assignment-type list above.
 */
export const MATERIAL_THUMBNAIL_OUTPUT_TYPES = [
    "Thumbnail10x10cm",
    "Thumbnail15x15cm",
    "Thumbnail20x20cm",
    "Thumbnail30x30cm",
    "Thumbnail7x7inches",
    "Thumbnail8x8inches",
    "Thumbnail9x9inches",
    "Thumbnail13_5x13_5inches",
    "Thumbnail16x16inches",
    "Thumbnail27x27inches",
    "Thumbnail32x32inches",
] as const

export type MaterialThumbnailOutputType = (typeof MATERIAL_THUMBNAIL_OUTPUT_TYPES)[number]

/**
 * How a thumbnail size is written for a person. Lives here rather than in the web app because
 * publish state is classified backend-side and its messages have to name the size the organization
 * actually publishes — "Thumbnail16x16inches" is a database value, not something to show anyone.
 */
export const MATERIAL_THUMBNAIL_OUTPUT_LABELS: Record<MaterialThumbnailOutputType, string> = {
    Thumbnail10x10cm: "10cm x 10cm",
    Thumbnail15x15cm: "15cm x 15cm",
    Thumbnail20x20cm: "20cm x 20cm",
    Thumbnail30x30cm: "30cm x 30cm",
    Thumbnail7x7inches: "7in x 7in",
    Thumbnail8x8inches: "8in x 8in",
    Thumbnail9x9inches: "9in x 9in",
    Thumbnail13_5x13_5inches: "13.5in x 13.5in",
    Thumbnail16x16inches: "16in x 16in",
    Thumbnail27x27inches: "27in x 27in",
    Thumbnail32x32inches: "32in x 32in",
}

export const MATERIAL_THUMBNAIL_DIMENSIONS: Record<MaterialThumbnailAssignmentType, MaterialThumbnailDimensions> = {
    [DataObjectAssignmentType.MaterialThumbnail_10x10]: {size: 10, resolution: 3000},
    [DataObjectAssignmentType.MaterialThumbnail_15x15]: {size: 15, resolution: 3000},
    [DataObjectAssignmentType.MaterialThumbnail_20x20]: {size: 20, resolution: 3000},
    [DataObjectAssignmentType.MaterialThumbnail_30x30]: {size: 30, resolution: 3000},
    [DataObjectAssignmentType.MaterialThumbnail_7x7Inches]: {size: 17.78, resolution: 2100},
    [DataObjectAssignmentType.MaterialThumbnail_8x8Inches]: {size: 20.32, resolution: 2400},
    [DataObjectAssignmentType.MaterialThumbnail_9x9Inches]: {size: 22.86, resolution: 3000},
    [DataObjectAssignmentType.MaterialThumbnail_13_5x13_5Inches]: {size: 34.29, resolution: 4050},
    [DataObjectAssignmentType.MaterialThumbnail_16x16Inches]: {size: 40.64, resolution: 4800},
    [DataObjectAssignmentType.MaterialThumbnail_27x27Inches]: {size: 68.58, resolution: 8100},
    [DataObjectAssignmentType.MaterialThumbnail_32x32Inches]: {size: 81.28, resolution: 9600},
}
